import { useFormContext } from 'react-hook-form';
import { Banner, Divider, DropdownItem, Stack, Typography } from '@optimus-web/core';
import { FormSelectField, FormSelectionCardGroupField, lt, useFeatureFlag } from '@mews/framework';
import { TooltippedSpan } from '@mews-commander/ui';
import { useCurrencyFormat } from '../../../../../../core/globalization/hooks/useCurrencyFormat';
import { PriceInput } from '../../../ConfigureProductForm/common/PriceInput';
import { PricingFieldNames, TaxesFieldNames } from '../../../ConfigureProductForm/formEnums';
import { useConfigureProductTax } from '../../../../hooks/useConfigureProductTax';
import { TaxRateSelectField } from '../../../ConfigureProductForm/steps/taxation/TaxRateSelectField';
import { groupedRepricingOptions, RepricingFieldName } from './repricingOptions';
import type { PricingProductFormValues, EditPricingProps } from './types';

type PricingFormContentPassedProps = Pick<EditPricingProps, 'value'>;

interface EditPricingFormContentProps extends PricingFormContentPassedProps {
    taxEnvironmentCode: string;
}

export const EditPricingFormContent = ({ value, taxEnvironmentCode }: EditPricingFormContentProps) => {
    const currencyFormat = useCurrencyFormat(value?.currency || '');
    const { getFieldState } = useFormContext<PricingProductFormValues>();
    const { isDirty } = getFieldState(PricingFieldNames.Price);
    const enterpriseProductRepricingEnabled = useFeatureFlag('enterpriseProductRepricingEnabled');

    const { searchTaxRates, taxConfigurationList } = useConfigureProductTax([taxEnvironmentCode]);

    return value ? (
        <Stack spacing="400" vertical>
            <Stack spacing="300" vertical>
                <Stack spacing="100" vertical>
                    <Typography textStyle="titleSmallStrong" as="div">
                        {lt('BasePrice')}
                    </Typography>
                    <Typography textStyle="bodyMedium" as="div">
                        {lt('EnterpriseProductEditPricingFormBasePriceDescription')}
                    </Typography>
                </Stack>
                <PriceInput
                    name={PricingFieldNames.Price}
                    label={lt('GeneralPriceExcludingTax')}
                    isDirty={isDirty}
                    prefix={currencyFormat.symbol}
                    defaultCurrencyCode={value.currency}
                />
            </Stack>
            <Stack spacing="300" vertical>
                <Stack spacing="100" vertical>
                    <Typography textStyle="titleSmallStrong" as="div">
                        {lt('EnterpriseProductEditPricingFormTaxesTitle')}
                    </Typography>
                    <Typography textStyle="bodyMedium" as="div">
                        {lt('EnterpriseProductEditPricingFormTaxesDescription')}
                    </Typography>
                </Stack>

                {taxConfigurationList.map(({ taxRates, code, name, localName, countryCode, environmentCode }) => (
                    <Stack spacing="200" distribution="stretch" key={code}>
                        <TooltippedSpan tooltip={localName || name}>
                            <FormSelectField
                                readOnly
                                label={lt('Tax')}
                                name={`${TaxesFieldNames.TaxationCode}.${code}`}
                                required
                            >
                                <DropdownItem label={`${localName || name} (${countryCode})`} value={code} />
                            </FormSelectField>
                        </TooltippedSpan>
                        <TaxRateSelectField
                            key={code}
                            code={code}
                            environmentCode={environmentCode}
                            searchTaxRates={searchTaxRates}
                            taxRates={taxRates}
                        />
                    </Stack>
                ))}
            </Stack>
            <Divider />
            {enterpriseProductRepricingEnabled ? (
                <FormSelectionCardGroupField
                    label={lt('EnterpriseProductEditPricingFormApplyNewPriceLabel')}
                    name={RepricingFieldName.RepriceExistingReservations}
                    type="radio"
                    options={groupedRepricingOptions}
                    required
                />
            ) : (
                <Banner
                    variant="info"
                    secondaryText={lt('EnterpriseProductEditPricingFormApplyNewPriceFutureInfoMessage')}
                />
            )}
        </Stack>
    ) : null;
};
